// Catalog.ID Client — Connected Apps Pane
// ============================================================================
// Lists OIDC relying party authorizations (consents). The row click opens the
// consent editor (scopes, shared claim types) — that is not destructive, so it
// stays. Revoking an app's access is destructive and now has its own button in
// the row; both it and the editor's "Revoke Access" run the same revoke path.

import { createListPaneType, createElement } from '@catalog-shell';
import type { ConfirmOptions, ListPaneConfig, PaneTypeRenderContext } from '@catalog-shell';
import { DataSource } from '@catalog-shell/components';
import type { ColumnDef, FetchResult } from '@catalog-shell/components';
import * as oidcApi from '../api/oidc';
import type { OidcConsentSummary } from '../api/types';
import { createRowActionColumn, type RowActionSpec } from '@catalog-shell';
import { PANE_IDS } from '../shell-config';

const columns: ColumnDef<OidcConsentSummary>[] = [
    {
        key: 'displayName',
        header: 'Application',
        width: 180,
        getValue: (item) => item.relyingParty.displayName,
        type: { kind: 'text' },
    },
    {
        key: 'displayDomain',
        header: 'Domain',
        width: 160,
        getValue: (item) => item.relyingParty.displayDomain,
        type: { kind: 'text' },
    },
    {
        key: 'scopes',
        header: 'Scopes',
        width: 140,
        getValue: (item) => item.scopes.join(', '),
        type: { kind: 'text' },
    },
    {
        key: 'lastAuthTime',
        header: 'Last Used',
        width: 100,
        sortable: true,
        getValue: (item) => item.lastAuthTime,
        type: {
            kind: 'text',
            converter: (v) => {
                try { return new Date(v as string).toLocaleDateString(); }
                catch { return String(v); }
            },
        },
    },
];

function createConsentsDataSource(
    remember: (items: OidcConsentSummary[]) => void,
): DataSource<OidcConsentSummary> {
    return new DataSource<OidcConsentSummary>({
        fetch: async (params): Promise<FetchResult<OidcConsentSummary>> => {
            const consents = await oidcApi.listConsents();
            const sliced = consents.slice(params.offset, params.offset + params.limit);
            remember(sliced);
            return { items: sliced, totalCount: consents.length };
        },
        keyFn: (item) => String(item.authorizationId),
        pageSize: 100,
    });
}

/** Pure: every authorization can be revoked, and that is the row's one verb. */
export function planConnectedAppRowAction(item: OidcConsentSummary): RowActionSpec | null {
    return {
        label: 'Revoke',
        variant: 'danger',
        title: `Revoke access for ${item.relyingParty.displayName}`,
    };
}

/**
 * Both the row button and the editor's "Revoke Access" ask first — every
 * destructive action in this client confirms. (The editor path could not
 * until the shell let a confirm stack on top of an open modal: Escape used
 * to close the modal underneath instead of the confirm.)
 */
export function connectedAppRevokeConfirm(item: OidcConsentSummary): ConfirmOptions {
    return {
        title: 'Revoke access?',
        message: `Revoke ${item.relyingParty.displayName} (${item.relyingParty.displayDomain})? `
            + 'It will have to ask for your consent again the next time you sign in with it.',
        confirmLabel: 'Revoke',
        cancelLabel: 'Cancel',
        confirmVariant: 'no',
        requireExplicitChoice: true,
    };
}

/**
 * The single revoke path, shared by the editor's "Revoke Access" and the row's
 * Revoke button. It also announces the change, which is what refreshes the
 * list — before, revoking left the revoked row on screen until a reload.
 */
async function revokeConsentAccess(
    shell: PaneTypeRenderContext['shell'],
    item: OidcConsentSummary,
): Promise<boolean> {
    try {
        await oidcApi.revokeConsent(item.authorizationId);
        shell.emit('data-changed', { type: 'oidc' });
        shell.toast(`Access revoked for ${item.relyingParty.displayName}`, 'success');
        return true;
    } catch (err) {
        shell.toast(err instanceof Error ? err.message : 'Failed to revoke', 'error');
        return false;
    }
}

/**
 * The column set is shared by every instance of this pane type, while the shell
 * to talk to belongs to the instance; the fetch that produced a row records it
 * against the row object — the only thing a cell click has in hand.
 */
const rowOrigin = new WeakMap<OidcConsentSummary, PaneTypeRenderContext['shell']>();

const revokeColumn = createRowActionColumn<OidcConsentSummary>({
    plan: planConnectedAppRowAction,
    execute: (item) => {
        const shell = rowOrigin.get(item);
        if (!shell) return;
        void (async () => {
            if (!await shell.confirm(connectedAppRevokeConfirm(item))) return;
            await revokeConsentAccess(shell, item);
        })();
    },
});

const config: ListPaneConfig<OidcConsentSummary> = {
    typeId: PANE_IDS.connectedApps,
    defaultTitle: 'Connected Apps',
    createDataSource: (ctx) => createConsentsDataSource((items) => {
        for (const item of items) rowOrigin.set(item, ctx.shell);
    }),
    columns: [...columns, revokeColumn],
    keyFn: (item) => String(item.authorizationId),
    initialMode: 'table',
    enabledModes: ['table'],
    selectionMode: 'none',
    onCreated: ({ dataSource }, ctx) => {
        ctx.disposables.add(ctx.shell.on('data-changed', (data) => {
            if ((data as { type?: string })?.type === 'oidc') {
                dataSource.refresh();
            }
        }));
    },
    onItemSelect: (item, ctx) => {
        void handleConsentAction(ctx.shell, item);
    },
    ariaLabel: 'Connected applications',
};

const ALL_SCOPES = ['openid', 'profile', 'email', 'phone', 'claims'];

async function handleConsentAction(
    shell: PaneTypeRenderContext['shell'],
    item: OidcConsentSummary,
): Promise<void> {
    const content = createElement('div', { className: 'cid-wizard-step' });

    content.appendChild(createElement('h3', {
        className: 'cid-wizard-heading',
        textContent: item.relyingParty.displayName,
    }));

    content.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: `Domain: ${item.relyingParty.displayDomain}`,
    }));

    // Editable scopes
    content.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Scopes:',
    }));

    const selectedScopes = new Set(item.scopes);
    for (const scope of ALL_SCOPES) {
        const row = createElement('div', { className: 'cid-auth-checkbox-row' });
        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.id = `consent-scope-${scope}`;
        cb.checked = selectedScopes.has(scope);
        if (scope === 'openid') {
            cb.disabled = true; // openid is always required
        }
        cb.addEventListener('change', () => {
            if (cb.checked) selectedScopes.add(scope);
            else selectedScopes.delete(scope);
        });
        const label = createElement('label', { textContent: scope });
        label.setAttribute('for', cb.id);
        row.appendChild(cb);
        row.appendChild(label);
        content.appendChild(row);
    }

    // Editable claim types
    const selectedClaims = new Set(item.consentedClaimTypes ?? []);
    if (selectedClaims.size > 0) {
        content.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: 'Shared claim types:',
        }));
        for (const ct of selectedClaims) {
            const row = createElement('div', { className: 'cid-auth-checkbox-row' });
            const cb = document.createElement('input');
            cb.type = 'checkbox';
            cb.id = `consent-claim-${ct}`;
            cb.checked = true;
            cb.addEventListener('change', () => {
                if (cb.checked) selectedClaims.add(ct);
                else selectedClaims.delete(ct);
            });
            const label = createElement('label', { textContent: ct });
            label.setAttribute('for', cb.id);
            row.appendChild(cb);
            row.appendChild(label);
            content.appendChild(row);
        }
    }

    const btnRow = createElement('div', { className: 'cid-pane-row' });

    const saveBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes',
        textContent: 'Save Changes',
    });
    const revokeBtn = createElement('button', {
        className: 'cs-btn cs-btn--no',
        textContent: 'Revoke Access',
    });
    const closeBtn = createElement('button', {
        className: 'cs-btn cs-btn--default',
        textContent: 'Close',
    });

    btnRow.appendChild(saveBtn);
    btnRow.appendChild(revokeBtn);
    btnRow.appendChild(closeBtn);
    content.appendChild(btnRow);

    const modal = shell.showModal(content);

    closeBtn.addEventListener('click', () => modal.close());

    saveBtn.addEventListener('click', async () => {
        saveBtn.setAttribute('disabled', '');
        try {
            await oidcApi.updateConsent(item.authorizationId, {
                scopes: Array.from(selectedScopes),
                consentedClaimTypes: selectedClaims.size > 0 ? Array.from(selectedClaims) : null,
            });
            modal.close();
            shell.toast('Consent updated', 'success');
        } catch (err) {
            saveBtn.removeAttribute('disabled');
            shell.toast(err instanceof Error ? err.message : 'Failed to update consent', 'error');
        }
    });

    revokeBtn.addEventListener('click', async () => {
        if (!await shell.confirm(connectedAppRevokeConfirm(item))) return;
        revokeBtn.setAttribute('disabled', '');
        if (await revokeConsentAccess(shell, item)) modal.close();
        else revokeBtn.removeAttribute('disabled');
    });
}

export function createConnectedAppsPane() {
    return createListPaneType(config);
}
